// Globals
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { selectQuery, partnersListSelector } from '../../feature/selectors';
import {
  deletePartner,
  getPartnerAuthData,
  getPartnersList,
  hidePartner,
  resendPartnerInvitation,
  togglePartnerActive,
} from '../../feature/actionCreators';
import { GetPartnersListDTO } from '../../dtos/';

// Models
import { IPartnerProfile } from '@/models/partners.model';

// Components
import { StyledContentBlock, StyledPageWrapper, StyledTitleModal, StyledButtonsWrapper } from '../../components/Styled';
import { HideModal, StatusModal } from '../../components';
import PartnersList from '../../components/PartnersList/PartnersList';
import { Button } from '@/ui';
import { Modal, ModalSize, showSuccessToast } from '@/components';

// Helpers
import { useModal, usePagination } from '@/hooks';
import { authWithToken } from '@/utils';
import { WEBSITE_ROUTES } from '@/constants/routes';

const PartnersListContainer = () => {
  const { isLoading, partnersList } = useAppState(partnersListSelector);
  const [currentPartnerDetails, setCurrentPartnerDetails] = useState<IPartnerProfile | null>(null);
  const { isOpened, openModal, closeModal } = useModal();
  const { isOpened: isStatusModalOpen, openModal: openStatusModal, closeModal: closeStatusModal } = useModal();
  const { isOpened: isHideModalOpen, openModal: openHideModal, closeModal: closeHideModal } = useModal();
  const { t } = useTranslation(['partners']);
  const { currentPage, onPageChange } = usePagination();
  const dispatch = useAppDispatch();
  const query = useAppState(selectQuery);

  const getPartnersListHandler = () => {
    const dto = new GetPartnersListDTO({ page: currentPage, ...query });
    dispatch(getPartnersList(dto));
    setCurrentPartnerDetails(null);
  };

  const handleDelete = async () => {
    try {
      if (currentPartnerDetails) await dispatch(deletePartner(currentPartnerDetails.id));
      getPartnersListHandler();
      closeModal();
      showSuccessToast({ message: t('partner_deleted_message') });
    } catch {}
  };

  const handleToggleActiveStatus = async () => {
    try {
      if (currentPartnerDetails) await dispatch(togglePartnerActive(currentPartnerDetails.id ?? ''));
      getPartnersListHandler();
      closeStatusModal();
    } catch {}
  };

  const handleHidePartner = async () => {
    try {
      if (currentPartnerDetails) await dispatch(hidePartner(currentPartnerDetails.id ?? ''));
      getPartnersListHandler();
      closeHideModal();
    } catch {}
  };

  const changePartnerForDelete = (partner: IPartnerProfile) => {
    setCurrentPartnerDetails(partner);
    openModal();
  };

  const changePartnerStatus = (partner: IPartnerProfile) => {
    setCurrentPartnerDetails(partner);
    openStatusModal();
  };

  const setHidePartner = (partner: IPartnerProfile) => {
    setCurrentPartnerDetails(partner);
    openHideModal();
  };

  const authAsPartner = async (id: string) => {
    const { data } = await dispatch(getPartnerAuthData(id)).unwrap();
    authWithToken(data?.access_token, WEBSITE_ROUTES.PARTNER_PROFILE);
  };

  const handleResendInvitation = async (id: string) => {
    try {
      await dispatch(resendPartnerInvitation(id)).unwrap();
      showSuccessToast({ message: t('partner_invitation_resent') });
      getPartnersListHandler();
    } catch {}
  };

  const handleCloseModal = () => {
    closeModal();
    setCurrentPartnerDetails(null);
  };

  useEffect(() => {
    getPartnersListHandler();
  }, [currentPage]);

  return (
    <StyledPageWrapper>
      <StyledContentBlock>
        <PartnersList
          isLoading={isLoading}
          currentPage={currentPage}
          onPageChange={onPageChange}
          authAsPartner={authAsPartner}
          setPartnerForDelete={changePartnerForDelete}
          setForStatusChange={changePartnerStatus}
          setHidePartner={setHidePartner}
          onResendInvitation={handleResendInvitation}
          total={partnersList?.meta?.total ?? 0}
          partnersList={partnersList?.data ?? []}
        />
      </StyledContentBlock>
      <Modal visible={isOpened} onCancel={handleCloseModal} size={ModalSize.LARGE}>
        <StyledTitleModal>
          {t('partners_delete_partner', {
            title: currentPartnerDetails?.title,
          })}
        </StyledTitleModal>
        <StyledButtonsWrapper>
          <Button onClick={handleCloseModal} type="primary">
            {t('partners_back')}
          </Button>
          <Button onClick={handleDelete} type="text">
            {t('partners_delete_button')}
          </Button>
        </StyledButtonsWrapper>
      </Modal>
      <StatusModal
        isOpened={isStatusModalOpen}
        closeModal={closeStatusModal}
        currentUserDetails={currentPartnerDetails as IPartnerProfile}
        handleToggleActiveStatus={handleToggleActiveStatus}
      />
      <HideModal
        isOpened={isHideModalOpen}
        closeModal={closeHideModal}
        currentUserDetails={currentPartnerDetails as IPartnerProfile}
        handleHidePartner={handleHidePartner}
      />
    </StyledPageWrapper>
  );
};

export default PartnersListContainer;
